/**
 * 배포 버전 스큐 감지 — 새 배포가 나가면 열려 있는 탭이 다음 라우트 이동에서
 * 전체 페이지 로드로 갈아타게 한다(__root.tsx beforeLoad 게이트). 오래 열린
 * 트레이딩 탭이 옛 번들로 계속 주문하는 시간을 줄이고, 오버랩 배포
 * (scripts/deploy-asset-overlap.ts)의 보존창 의존도도 낮춘다.
 *
 * 서버 소스: 배포 때 생성되는 /version.json ({ release }). 과거 배포엔 이
 * 파일이 없어 Pages SPA fallback 이 text/html 200 을 주므로 content-type 을
 * 반드시 검사한다. dev/프리뷰 빌드는 VITE_SENTRY_RELEASE 가 없어 전체가 no-op.
 *
 * 스테일 판정은 원웨이 래치 — 한 번 true 면 리스너를 정리하고 그대로 유지.
 * 실제 리로드는 라우터 beforeLoad 가 canReloadNow()(30초 빈도 가드, ←
 * preload-error-handler.ts 의 RELOAD_KEY 패턴)와 함께 수행한다.
 */

const CHECK_MIN_GAP_MS = 60_000;
const CHECK_INTERVAL_MS = 15 * 60_000;
const RELOAD_KEY = "version-skew-reloaded-at";
const RELOAD_GUARD_MS = 30_000;

/**
 * /version.json 응답이 "새 버전 배포됨"을 뜻하는지 순수 판정.
 * SPA fallback(HTML)·비정상 페이로드·동일 릴리즈는 전부 false.
 */
export function evaluateVersionPayload(
	contentType: string | null,
	payload: unknown,
	clientRelease: string,
): boolean {
	if (!contentType?.includes("application/json")) return false;
	if (typeof payload !== "object" || payload === null) return false;
	const release = (payload as { release?: unknown }).release;
	return (
		typeof release === "string" && release !== "" && release !== clientRelease
	);
}

let stale = false;

export function isVersionStale(): boolean {
	return stale;
}

/** 리로드 빈도 가드 — true 를 반환하며 타임스탬프를 소비(기록)한다. */
export function canReloadNow(): boolean {
	// __root.tsx beforeLoad 경유로 서버(빌드타임 셸 프리렌더 포함)에서도 호출될
	// 수 있다 — 리로드는 브라우저 개념이므로 항상 false.
	if (typeof sessionStorage === "undefined") return false;
	const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
	if (Date.now() - last <= RELOAD_GUARD_MS) return false;
	sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
	return true;
}

export function initVersionCheck() {
	const clientRelease = import.meta.env.VITE_SENTRY_RELEASE;
	if (!import.meta.env.PROD || !clientRelease) return;

	let lastCheckAt = 0;
	let intervalId: ReturnType<typeof setInterval> | undefined;

	const teardown = () => {
		window.removeEventListener("focus", check);
		document.removeEventListener("visibilitychange", onVisible);
		clearInterval(intervalId);
	};

	async function check() {
		if (stale) return;
		const now = Date.now();
		if (now - lastCheckAt < CHECK_MIN_GAP_MS) return;
		lastCheckAt = now;
		try {
			const res = await fetch("/version.json", { cache: "no-store" });
			if (!res.ok) return;
			const contentType = res.headers.get("content-type");
			if (!contentType?.includes("application/json")) return;
			const payload: unknown = await res.json();
			if (evaluateVersionPayload(contentType, payload, clientRelease)) {
				stale = true;
				teardown();
			}
		} catch {
			// 오프라인/일시 오류 — 다음 트리거에서 재시도
		}
	}

	function onVisible() {
		if (document.visibilityState === "visible") void check();
	}

	window.addEventListener("focus", check);
	document.addEventListener("visibilitychange", onVisible);
	intervalId = setInterval(check, CHECK_INTERVAL_MS);
}
